import React from 'react';
import { Button, Divider, Flex, Form, Input, InputNumber, Switch } from 'antd';
import { READ_ONLY_INPUT_STYLE } from '@leedpulse-monorepo/components';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import RecordInput from '../../@shared/inputs/RecordInput';
import { EFieldType } from '../../../../domain/enums/admin/AdminFieldType';
import CustomComponents from './custom_components';

const formatLabel = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export interface AdminFieldInputProps {
  type: EFieldType;
  namePath: (string | number)[];
  label?: React.ReactNode;
  extra?: React.ReactNode;
  value?: unknown;
  isReadOnly?: boolean;
  onRemove?: (path: (string | number)[]) => void;
  onAdd?: (path: (string | number)[], existingKeys: string[]) => void;
  onSet?: (path: (string | number)[], value: unknown) => void; // writes the form and the tab schema, so added or removed keys reach the payload
}

const AdminFieldInput: React.FC<AdminFieldInputProps> = ({
  type,
  namePath,
  label,
  extra,
  value,
  isReadOnly = false,
  onRemove,
  onAdd,
  onSet,
}) => {
  if (type === EFieldType.BOOLEAN) {
    return (
      <Form.Item label={label} extra={extra} name={namePath} valuePropName="checked">
        <Switch disabled={isReadOnly} />
      </Form.Item>
    );
  }

  if (type === EFieldType.NUMBER) {
    return (
      <Form.Item label={label} extra={extra} name={namePath}>
        <InputNumber readOnly={isReadOnly} style={isReadOnly ? READ_ONLY_INPUT_STYLE : { width: '100%' }} />
      </Form.Item>
    );
  }

  if (type === EFieldType.OBJECT) {
    return (
      <Form.Item label={label} extra={extra}>
        <RecordInput
          namePath={namePath}
          data={(value ?? {}) as Record<string, unknown>}
          isReadOnly={isReadOnly}
          onRemove={onRemove}
          onAdd={onAdd}
        />
      </Form.Item>
    );
  }

  if (type === EFieldType.ARRAY) {
    const list = Array.isArray(value) ? value : [];
    const firstItem = list[0];
    const subKeys =
      firstItem && typeof firstItem === 'object' && !Array.isArray(firstItem)
        ? Object.keys(firstItem as Record<string, unknown>)
        : null;

    return (
      <Form.Item label={label} extra={extra}>
        <Flex
          vertical
          gap={16}
          style={{
            width: '100%',
            padding: '8px 0',
            borderRadius: 8,
            border: '1px solid var(--background-color)',
          }}
        >
          <Form.List name={namePath}>
            {(fields, { add, remove }) => (
              <>
                <Flex vertical style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <React.Fragment key={key}>
                      {index > 0 && <Divider size="small" />}
                      <Flex align="center" justify="space-between" gap={16} style={{ padding: '0 8px' }}>
                        {subKeys?.map((subKey) => (
                          <Form.Item
                            {...restField}
                            key={subKey}
                            label={formatLabel(subKey)}
                            name={[name, subKey]}
                            rules={[{ required: true, message: `${formatLabel(subKey)} requis` }]}
                            style={{ flex: 1 }}
                          >
                            <Input readOnly={isReadOnly} style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}} />
                          </Form.Item>
                        ))}
                        <Form.Item label=" ">
                          <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'var(--danger-color)' }} />
                        </Form.Item>
                      </Flex>
                    </React.Fragment>
                  ))}
                </Flex>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add(subKeys ? Object.fromEntries(subKeys.map((k) => [k, ''])) : {})}
                  style={{ margin: '16px 8px 0 8px' }}
                >
                  Ajouter
                </Button>
              </>
            )}
          </Form.List>
        </Flex>
      </Form.Item>
    );
  }

  if (type === EFieldType.CUSTOM) {
    const CustomFieldComponent = CustomComponents[(value as Record<string, unknown>).custom_field_component as keyof typeof CustomComponents] as React.FC<AdminFieldInputProps> | null;
    if (!CustomFieldComponent) return null;

    return (
      // <Form.Item label={label} extra={extra} name={namePath}>
      <CustomFieldComponent
        type={type}
        namePath={namePath}
        label={label}
        extra={extra}
        value={value}
        isReadOnly={isReadOnly}
        onRemove={onRemove}
        onAdd={onAdd}
        onSet={onSet}
      />
      // </Form.Item>
    );
  }

  return (
    <Form.Item label={label} extra={extra} name={namePath}>
      <Input readOnly={isReadOnly} style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}} />
    </Form.Item>
  );
};

export default AdminFieldInput;
